/**
 * One follow-up move ("Thank them for a referral", "Check in"...) with the
 * three things you can do about it: have it written for you, mark it done
 * (with an optional note), or skip it.
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ChevronUp, ExternalLink, Mail, RefreshCw, SkipForward, WandSparkles } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Avatar, Button, Input, Textarea, useToast } from "@/components/ui";
import { Pill } from "@/features/command/ui";
import { CopyButton, KIND_META, TONE_CLASS, dueOf, kindLabel, profileUrl } from "./shared";
import type { Handle, Move, Rhythm } from "./types";

export function MoveItem({
  move, person, onChanged, rhythm, compact, email: emailProp, handles: handlesProp, onCollapse,
}: {
  move: Move;
  /** Show who it's for (the list page). The person page leaves this out. */
  person?: { id: string; name: string; kind?: string | null } | null;
  /** Their email + handles, for the mailto link and "open their profile". */
  email?: string | null;
  handles?: Handle[];
  onChanged: () => void;
  rhythm?: Partial<Record<string, Rhythm>> | null;
  compact?: boolean;
  /** Folds an expanded "coming up" row back into one line. */
  onCollapse?: () => void;
}) {
  const toast = useToast();
  const { pathname, search } = useLocation();
  const [draft, setDraft] = useState<string | null>(null);
  /** What the server has, so an edit is only sent when it changed. */
  const [savedText, setSavedText] = useState<string | null>(move.draft ?? null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [drafting, setDrafting] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"done" | "skip" | null>(null);

  const email = emailProp ?? move.email ?? null;
  const handles = handlesProp ?? move.handles ?? [];
  const firstProfile = handles.map(profileUrl).find(Boolean) ?? null;
  const due = dueOf(move.due_date);
  const meta = person?.kind ? KIND_META[person.kind as keyof typeof KIND_META] : null;

  const write = async (refresh = false) => {
    setDrafting(true);
    try {
      const d = await api.post<{ text: string }>(`/affiliates/moves/${move.id}/draft`, refresh ? { refresh: true } : {});
      setDraft(d.text);
      setSavedText(d.text);
      setSaveState("idle");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't write it");
    } finally { setDrafting(false); }
  };

  /** Keep an edited message (on blur, Copy or Email it) so it survives a reload. */
  const persist = async () => {
    if (draft == null || draft === savedText) return;
    const text = draft;
    setSaveState("saving");
    try {
      await api.patch(`/affiliates/moves/${move.id}`, { draft: text });
      setSavedText(text);
      setSaveState("saved");
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1600);
    } catch (err) {
      setSaveState("idle");
      toast.error(err instanceof ApiError ? err.message : "Couldn't save your edit");
    }
  };

  const finish = async (kind: "done" | "skip") => {
    setBusy(kind);
    try {
      await api.post(`/affiliates/moves/${move.id}/${kind}`, kind === "done" && note.trim() ? { note: note.trim() } : {});
      toast.success(kind === "done" ? "Nice, marked done" : "Skipped");
      setDoneOpen(false);
      setNote("");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    } finally { setBusy(null); }
  };

  const mailto = email && draft != null
    ? `mailto:${encodeURIComponent(email)}?body=${encodeURIComponent(draft)}`
    : null;

  return (
    <div className={cn("rounded-xl border border-zinc-800/70 bg-zinc-900/40", compact ? "p-3" : "p-3 sm:p-3.5")}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {person && <Avatar name={person.name} size="sm" className="mt-0.5" />}
          <div className="min-w-0">
            {person && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Link
                  to={`/affiliates/${person.id}`}
                  state={{ from: pathname + search }}
                  className="truncate text-sm font-semibold text-zinc-100 hover:text-indigo-300"
                >
                  {person.name}
                </Link>
                {meta && person.kind && <Pill color={meta.color}>{kindLabel(person.kind, rhythm)}</Pill>}
              </div>
            )}
            <div className={cn("flex flex-wrap items-baseline gap-x-2", person ? "mt-0.5" : "")}>
              <span className={cn("text-sm", person ? "text-zinc-300" : "font-medium text-zinc-100")}>{move.label}</span>
              <span className={cn("text-xs font-medium", TONE_CLASS[due.tone])}>{due.text}</span>
            </div>
            {move.note && <div className="mt-0.5 text-xs text-zinc-500">{move.note}</div>}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant={draft == null ? "secondary" : "ghost"}
            leftIcon={<WandSparkles className="h-3.5 w-3.5" />}
            loading={drafting && draft == null}
            onClick={() => {
              if (draft == null) { void write(); return; }
              void persist();
              setDraft(null);
            }}
          >
            {draft != null ? "Hide message" : savedText ? "Open the message" : "Write it for me"}
          </Button>
          <Button size="sm" variant="outline" leftIcon={<Check className="h-3.5 w-3.5" />} onClick={() => setDoneOpen((v) => !v)}>
            Done
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<SkipForward className="h-3.5 w-3.5" />}
            loading={busy === "skip"}
            onClick={() => void finish("skip")}
          >
            Skip
          </Button>
          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              title="Fold away"
              aria-label="Fold away"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {draft != null && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void persist()}
            rows={4}
            className="text-[13px] leading-relaxed"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            <CopyButton text={draft} onCopy={() => void persist()} />
            {mailto && (
              <a href={mailto} onClick={() => void persist()} className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100">
                <Mail className="h-3.5 w-3.5" /> Email it
              </a>
            )}
            {move.kind === "engage" && firstProfile && (
              <a href={firstProfile} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100">
                <ExternalLink className="h-3.5 w-3.5" /> Open their profile
              </a>
            )}
            <button
              type="button"
              onClick={() => void write(true)}
              disabled={drafting}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100 disabled:opacity-50"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", drafting && "animate-spin")} /> Write another
            </button>
            {!email && <span className="text-[11px] text-zinc-500">No email on file, copy it into a DM.</span>}
            {saveState !== "idle" && (
              <span className={cn("ml-auto text-[11px]", saveState === "saved" ? "text-emerald-300" : "text-zinc-500")}>
                {saveState === "saved" ? "Edit saved" : "Saving..."}
              </span>
            )}
          </div>
        </div>
      )}

      {doneOpen && (
        <form
          className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center"
          onSubmit={(e) => { e.preventDefault(); void finish("done"); }}
        >
          <div className="min-w-0 flex-1">
            <Input autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to remember? (optional)" />
          </div>
          <div className="flex gap-1.5">
            <Button type="submit" size="sm" loading={busy === "done"} leftIcon={<Check className="h-3.5 w-3.5" />}>Mark done</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setDoneOpen(false)}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
